import React, { useState, useEffect } from 'react';
import './bookingQuestionsForm.css';
import PolarSwitch from '../common/formComponents/polarSwitch';
import { SubmitButton } from '../common/formComponents';

/**
 * Renders a list of booking questions and manages user responses.
 *
 * Props:
 * - questions: array of question objects defining the form layout
 * - onSubmit: function called when the internal form is submitted
 * - onChange: function invoked whenever responses change
 * - showSubmit: display the internal submit button when true
 * - initialResponses: existing answers to prefill the form, typically for editing
 */
export default function BookingQuestionsForm({
  questions = [],
  onSubmit,
  onChange,
  showSubmit = true,
  initialResponses
}) {
  // Track answers keyed by question text so they can be persisted easily.
  const [responses, setResponses] = useState(initialResponses || {});

  // Sync local state when a fresh set of initial responses is provided.
  useEffect(() => {
    if (initialResponses) {
      setResponses(initialResponses);
    }
  }, [initialResponses]);

  // Notify parent components of response changes when requested.
  useEffect(() => {
    if (onChange) onChange(responses);
  }, [responses, onChange]);

  // Update the response for a specific question.
  const update = (questionText, value) => {
    setResponses(prev => ({ ...prev, [questionText]: value }));
  };

  // Forward all responses to the parent when the form is submitted.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(responses);
  };

  // Render each supported question type using a two-column layout so inputs
  // align consistently within modals.
  const renderQuestion = (q) => {
    const title = q.question || q.questionText || '';
    const val = responses[title] ?? (q.questionType === 'polarSwitch' ? false : '');
    const setVal = (v) => update(title, v);

    switch (q.questionType) {
      case 'singleLineText':
        return (
          <div key={q.id} className='optionRow questionRow'>
            <span className='optionLabel'>{title}</span>
            <input
              type='text'
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className='questionInput'
            />
          </div>
        );
      case 'multiLineText':
        return (
          <div key={q.id} className='optionRow questionRow'>
            <span className='optionLabel'>{title}</span>
            <textarea
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className='questionTextArea'
            />
          </div>
        );
      case 'date':
        return (
          <div key={q.id} className='optionRow questionRow'>
            <span className='optionLabel'>{title}</span>
            <input
              type='date'
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className='questionInput questionDateInput'
            />
          </div>
        );
      case 'polarSwitch':
        return (
          <div key={q.id} className='optionRow questionRow'>
            <span className='optionLabel'>{title}</span>
            <PolarSwitch data={val} setData={setVal} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <form className='bookingQuestionsForm' onSubmit={handleSubmit}>
      {questions.map(renderQuestion)}
      {showSubmit && <SubmitButton text='Submit' />}
    </form>
  );
}

